import { List as ImmutableList, Set as ImmutableSet } from 'immutable';
import PropTypes from 'prop-types';
// CORE
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsSelectors } from 'app-components/flux/chart-of-accounts';
import { tagsSelectors } from 'app-components/flux/tags';

import CheckIcon from '@mui/icons-material/Check';
import ArrowDropDown from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUp from '@mui/icons-material/ArrowDropUpRounded';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import { makeStyles } from 'tss-react/mui';

// COMPONENTS
import QResourcePicker from 'components/QResourcePicker';
import QTip from 'components/QuickenControls/QTip';

import { getNameFromKeyFactory } from 'components/QFilterSelect/utils';
// DATA & UTILS
import { createFilterData } from 'containers/SpendingWatchlist/utils';

// LOCAL
import { payeesSelectors } from 'app-components/flux/payees';
import ChipList from '../ChipList';

const useStyles = makeStyles()(({ _palette }) => ({
  root: {
    position: 'relative',
    minWidth: 78,
    marginTop: 0,
    outline: 'none',
    '& *': {
      cursor: 'pointer !important',
    },
  },
  minHeight: {
    minHeight: 40,
  },
  fab: {
    zIndex: 2,
    minHeight: 32,
    height: 40,
    width: 40,
  },

  // popover internal styles bellow here
  popoverPaper: {
    maxHeight: 500,
    overflowY: 'auto',
    width: 410,
    height: 560,
    padding: 10,
    display: 'flex',
    transform: 'translateY(-27px) !important',
  },
  chipContainerWithScroll: {
    margin: 0,
    maxHeight: 130,
    boxSizing: 'border-box',
    marginLeft: 'auto',
    marginRight: 16,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
  },
  textFieldMulti: {
    maxWidth: 450,
    width: 410,
    display: 'flex',
    flexWrap: 'wrap',
    borderRadius: 12,
  },
}));

const getDescendantIds = (node) => {
  const descendantIds = [];
  const stack = node.children ? [...node.children] : [];

  while (stack.length > 0) {
    const current = stack.pop();
    if (current?.id) descendantIds.push(current.id);
    if (current?.children) stack.push(...current.children);
  }

  return descendantIds;
};

const syncSelectedUsageParents = (selectedItems, chartOfAccounts) => {
  let syncedItems = selectedItems;

  chartOfAccounts.forEach((node) => {
    if (node.coa?.type !== 'CATEGORY_USAGE_TYPE' || !node.id) return;

    const descendantIds = getDescendantIds(node);
    syncedItems =
      descendantIds.length > 0 && descendantIds.every((categoryId) => selectedItems.has(categoryId))
        ? syncedItems.add(node.id)
        : syncedItems.delete(node.id);
  });

  return syncedItems;
};

const convertFilterToSelectedItems = (filter, chartOfAccounts) => {
  switch (filter.type) {
    case 'PAYEE': {
      return filter.payees ? ImmutableSet(filter.payees.map((s) => s.toLowerCase())) : ImmutableSet();
    }
    case 'TAG':
      return filter.tags ? ImmutableSet(filter.tags.map(({ id }) => id)) : ImmutableSet();
    case 'CHARTOFACCOUNT':
      return filter.chartOfAccounts
        ? syncSelectedUsageParents(
            ImmutableSet(
              filter.chartOfAccounts.map(({ id, type }) =>
                id === '0' && type === 'UNCATEGORIZED' ? 'UNCATEGORIZED' : id,
              ),
            ),
            chartOfAccounts,
          )
        : ImmutableSet();
    default:
      return new ImmutableSet();
  }
};

const getSerializableSelectedItems = (itemsType, selectedItems, getNameForItem) => {
  switch (itemsType) {
    case 'payeeNames':
      return selectedItems.map((item) => getNameForItem('payees', item));
    case 'categories':
      return selectedItems.filter((item) => !item.startsWith('USAGE-'));
    default:
      return selectedItems;
  }
};

const filterResources = (item) => !item.id?.includes('BALANCE_ADJUSTMENT');

const getInputProps = (popoverEl, collapsedChipList, handleRemoveChip, id, onBlur, onFocus) => ({
  autoComplete: 'off',
  inputComponent: ChipList,
  inputProps: {
    id,
    chips: collapsedChipList,
    maxChips: 100,
    onRemoveChip: handleRemoveChip,
    expandable: false,
    zeroDiv: true,
    showChips: popoverEl === null,
    hasEndAdornment: true,
    onBlur,
    onFocus,
  },
  endAdornment: (
    <InputAdornment
      position='end'
      disablePointerEvents
      sx={{ color: 'action.active', position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}
    >
      {popoverEl !== null ? <ArrowDropUp /> : <ArrowDropDown />}
    </InputAdornment>
  ),
});

const TypeSelectField = (props) => {
  const { id, error, helperText, onBlur: onDismiss, onChange, itemsType, filter, filterTypeKey, label } = props;
  const [popoverEl, setPopoverEl] = useState(null);
  const { classes } = useStyles({ popoverEl });

  const selectRef = useRef();
  const [focused, setFocus] = useState(null);

  const categoriesById = useSelector(categoriesSelectors.getCategoriesById);
  const chartOfAccounts = useSelector(chartOfAccountsSelectors.getChartOfAccountsTree);
  const payeesByName = useSelector(payeesSelectors.getPayeesForTransactions);
  const tagsById = useSelector(tagsSelectors.getTags);
  const hydratedFilterTypeKeyRef = useRef(null);

  const [selectedItems, setSelectedItems] = useState(ImmutableSet());

  useEffect(() => {
    const needsCategoryTree =
      filter?.type === 'CHARTOFACCOUNT' && filter.chartOfAccounts?.some(({ type }) => type === 'CATEGORY');
    const categoryTreeIsReady = !needsCategoryTree || chartOfAccounts.size > 0;
    if (hydratedFilterTypeKeyRef.current !== filterTypeKey && filter && categoryTreeIsReady) {
      const initialSelectedItems = convertFilterToSelectedItems(filter, chartOfAccounts);
      setSelectedItems(initialSelectedItems);
      hydratedFilterTypeKeyRef.current = filterTypeKey;
    }
  }, [filterTypeKey, filter, chartOfAccounts]);

  const onOpenPicker = useCallback(() => {
    setPopoverEl(selectRef.current);
  }, [setPopoverEl, selectRef]);

  const onClosePopover = useCallback(() => {
    onDismiss?.();
    setPopoverEl(null);
    setTimeout(() => selectRef.current?.focus(), 0);
  }, [setPopoverEl, onDismiss]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getNameForItem = useCallback(
    getNameFromKeyFactory(categoriesById, payeesByName, tagsById, null, chartOfAccounts),
    [categoriesById, payeesByName, tagsById, chartOfAccounts],
  );

  const handleAddChip = useCallback(
    (newSet, selectedName) => {
      const updatedSet = itemsType === 'categories' ? syncSelectedUsageParents(newSet, chartOfAccounts) : newSet;
      setSelectedItems(updatedSet);
      const filteredData = getSerializableSelectedItems(itemsType, updatedSet, getNameForItem);
      const newFilterData = createFilterData(itemsType, filteredData);

      onChange(newFilterData, selectedName);
    },
    [itemsType, onChange, getNameForItem, chartOfAccounts],
  );

  const handleRemoveChip = useCallback(
    (removedItem) => {
      let updatedSet = selectedItems.delete(removedItem.id);
      if (itemsType === 'categories') {
        const childIds =
          categoriesSelectors.getChildCategoriesByParentIdUtil(categoriesById, removedItem.id).keySeq().toArray() || [];
        if (childIds.length > 0) updatedSet = updatedSet.subtract(childIds);
        const usageNode = chartOfAccounts.find((node) => node.id === removedItem.id);
        if (usageNode?.coa?.type === 'CATEGORY_USAGE_TYPE') {
          updatedSet = updatedSet.subtract(getDescendantIds(usageNode));
        }
        updatedSet = syncSelectedUsageParents(updatedSet, chartOfAccounts);
      }
      setSelectedItems(updatedSet);
      const filteredData = getSerializableSelectedItems(itemsType, updatedSet, getNameForItem);
      const newFilterData = createFilterData(itemsType, filteredData);
      onChange(newFilterData);
    },
    [selectedItems, itemsType, categoriesById, getNameForItem, onChange, chartOfAccounts],
  );

  const collapsedChipList = useMemo(() => {
    let retList = ImmutableList();
    if (itemsType === 'categories') {
      const selectedUsageDescendantIds = chartOfAccounts.reduce(
        (descendantIds, node) =>
          node.coa?.type === 'CATEGORY_USAGE_TYPE' && node.id && selectedItems.has(node.id)
            ? descendantIds.union(getDescendantIds(node))
            : descendantIds,
        ImmutableSet(),
      );
      retList = selectedItems
        .filter((catId) => !selectedUsageDescendantIds.has(catId))
        .map((catId) => ({
          id: catId,
          name: getNameForItem('categories', catId),
          parentId: categoriesById.get(catId)?.parentId,
        }))
        .filter((item) => !item.parentId || !selectedItems.has(item.parentId));
    } else if (itemsType === 'payeeNames') {
      retList = selectedItems.map((payeeId) => ({ id: payeeId, name: getNameForItem('payees', payeeId) }));
    } else if (itemsType === 'tags') {
      retList = selectedItems.map((tagId) => ({ id: tagId, name: getNameForItem('tags', tagId) }));
    }
    return retList.toArray();
  }, [selectedItems, getNameForItem, itemsType, categoriesById, chartOfAccounts]);

  const onTextFieldClick = useCallback(() => {
    if (!popoverEl) {
      onOpenPicker();
    }
  }, [popoverEl, onOpenPicker]);

  const onBlur = useCallback(() => {
    setFocus(false);
  }, []);
  const onFocus = useCallback(() => {
    setFocus(true);
  }, []);

  const inputProps = useMemo(
    () => getInputProps(popoverEl, collapsedChipList, handleRemoveChip, id, onBlur, onFocus),
    [popoverEl, collapsedChipList, handleRemoveChip, id, onBlur, onFocus],
  );

  return (
    <div className={classes.root}>
      <TextField
        inputRef={selectRef}
        label={label}
        value={collapsedChipList}
        multiline
        error={error}
        helperText={helperText}
        InputProps={inputProps}
        onClick={onTextFieldClick}
        className={classes.textFieldMulti}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            onTextFieldClick();
          }
        }}
        variant='filled'
        margin={'normal'}
        focused={Boolean(popoverEl) || document.activeElement === selectRef?.current || focused}
      ></TextField>
      <Popover
        id='type-select-picker-popover'
        open={Boolean(popoverEl)}
        anchorEl={popoverEl}
        onClose={onClosePopover}
        placement={'top-start'}
        classes={{
          paper: classes.popoverPaper,
        }}
      >
        <QResourcePicker
          autoFocus
          noHeader
          selectedItems={selectedItems}
          onSelect={handleAddChip}
          resourceType={itemsType}
          pageSize={12}
          filterFn={filterResources}
          noBorder
          noSpecial
        >
          {collapsedChipList.length > 0 ? (
            <Box display={'flex'} margin={'4px 0'}>
              <ChipList chips={collapsedChipList} maxChips={5} onRemoveChip={handleRemoveChip} zeroDiv={false} />
              <div className={classes.chipContainerWithScroll}>
                <QTip title='Done' placement='top' distance={0}>
                  <Fab
                    color='primary'
                    aria-label='close-chooser'
                    id='close-chooser'
                    className={classes.fab}
                    onClick={onClosePopover}
                    size='small'
                  >
                    <CheckIcon className={classes.minHeight} />
                  </Fab>
                </QTip>
              </div>
            </Box>
          ) : null}
        </QResourcePicker>
      </Popover>
    </div>
  );
};

TypeSelectField.propTypes = {
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  itemsType: PropTypes.string,
  filter: PropTypes.object,
  filterTypeKey: PropTypes.string,
  label: PropTypes.string,
  onBlur: PropTypes.func, // Added onBlur to propTypes validation
};

TypeSelectField.whyDidYouRender = true;

export default TypeSelectField;
